import { useEffect, useMemo, useState } from "react";
import { batchApi } from "../../api/batchApi";
import { analyticsScopeFilters, hasAnalyticsTimeFilter, matchesAnalyticsBatch, matchesMachine } from "../../components/analytics/analyticsFilters";
import { telemetryFromCycles } from "../realTimeAnalytics/realTimeAnalyticsData";
import { getMachineAnalytics } from "./machineAnalyticsData";

export async function loadPeriodMachineAnalytics(filters, machines, batches, plantId) {
  const scope = analyticsScopeFilters(filters);
  const machineIds = new Set(machines.filter((machine) => matchesMachine(machine, scope, plantId)).map((machine) => machine.machine_id));
  const selected = batches.filter((batch) => machineIds.has(batch.raw?.machine_id) && matchesAnalyticsBatch(batch, scope, plantId));
  const summaries = [];
  let next = 0;
  // Bound parallel requests on plants with many batches.
  const worker = async () => {
    while (next < selected.length) {
      const batch = selected[next++];
      const { summary } = telemetryFromCycles(batch, await batchApi.getAllCycles(batch.uid), filters);
      summaries.push({ ...batch, raw: { ...batch.raw, total_cycles: summary.totalCycle, ok_parts: summary.okParts } });
    }
  };
  await Promise.all(Array.from({ length: Math.min(4, selected.length) }, worker));
  return getMachineAnalytics(scope, machines, summaries, plantId);
}

export default function useMachineAnalytics(filters, machines, batches, plantId) {
  const periodOnly = hasAnalyticsTimeFilter(filters);
  const fullPeriod = useMemo(() => getMachineAnalytics(filters, machines, batches, plantId), [filters, machines, batches, plantId]);
  const [period, setPeriod] = useState({ key: null, rows: [], error: null, isLoading: false });
  const key = useMemo(() => ({ filters, machines, batches, plantId }), [filters, machines, batches, plantId]);
  useEffect(() => {
    if (!periodOnly) return undefined;
    let active = true;
    setPeriod({ key, rows: [], error: null, isLoading: true });
    loadPeriodMachineAnalytics(filters, machines, batches, plantId)
      .then((rows) => { if (active) setPeriod({ key, rows, error: null, isLoading: false }); })
      .catch((error) => { if (active) setPeriod({ key, rows: [], error, isLoading: false }); });
    return () => { active = false; };
  }, [periodOnly, key, filters, machines, batches, plantId]);
  if (!periodOnly) return { rows: fullPeriod, isLoading: false, error: null };
  return period.key === key ? period : { rows: [], isLoading: true, error: null };
}
